import type { api } from "@breakout/backend/convex/_generated/api"
import { formatTimeAgoSec } from "@breakout/shared/utils/time"
import { cn, formatShares } from "@breakout/ui/utils"
import { Link } from "@tanstack/react-router"
import type { FunctionReturnType } from "convex/server"
import { ExternalLink, Loader2 } from "lucide-react"
import { motion } from "motion/react"
import { memo } from "react"
import type { Address } from "viem"
import { useInfiniteScroll } from "../../hooks/common/useInfiniteScroll"
import { useMarketActivity } from "../../hooks/indexer/useMarketActivity"
import { BINARY_LABELS } from "../../lib/constants"
import { breakoutChain } from "../../lib/providers"
import type { OutcomeBucket } from "../../lib/types"
import { ActivityRowSkeleton } from "../ui/ActivityRowSkeleton"
import { SimpleAvatar } from "../ui/SimpleAvatar"

type MarketTrade = FunctionReturnType<typeof api.indexer.public.getMarketActivity>["page"][number]

interface ActivityFeedProps {
	marketAddress: Address | undefined
	/** Outcome buckets with labels and directions - passed from parent to avoid redundant fetching */
	buckets: OutcomeBucket[]
}

/**
 * Activity Feed component with infinite scroll
 *
 * Displays recent trades for a market.
 * Each row shows trader, action (bought/sold), outcome, price, amount, and tx link.
 */
export const ActivityFeed = memo(function ActivityFeed({
	marketAddress,
	buckets,
}: ActivityFeedProps) {
	const { results, status, loadMore, isLoading, hasMore } = useMarketActivity(
		marketAddress ? { marketAddress } : null,
	)

	const loadMoreRef = useInfiniteScroll({
		hasMore,
		status,
		loadMore,
	})

	// Show skeleton while loading first page
	const showLoadingSkeleton = (isLoading && results.length === 0) || status === "LoadingFirstPage"
	if (showLoadingSkeleton) {
		return (
			<div className="w-full space-y-1">
				{[0, 1, 2, 3, 4].map(n => (
					<ActivityRowSkeleton key={`skeleton-${n}`} variant="market" />
				))}
			</div>
		)
	}

	if (status === "Exhausted" && results.length === 0) {
		return (
			<div className="w-full py-6 text-center">
				<p className="text-sm text-text-muted">No trades yet</p>
			</div>
		)
	}

	return (
		<div className="w-full">
			<div className="space-y-1">
				{results.map((trade, index) => (
					<motion.div
						animate={{ opacity: 1, x: 0 }}
						initial={{ opacity: 0, x: -10 }}
						key={`${trade.txHash}-${trade.bucket}`}
						transition={{ duration: 0.2, delay: index * 0.05 }}
					>
						<TradeRow buckets={buckets} trade={trade} />
					</motion.div>
				))}
			</div>

			{/* Load more sentinel */}
			<div className="py-4" ref={loadMoreRef}>
				{status === "LoadingMore" && (
					<div className="flex items-center justify-center gap-2 py-2 text-sm text-text-muted">
						<Loader2 aria-hidden="true" className="size-4 animate-spin" />
						<span>Loading more…</span>
					</div>
				)}
			</div>
		</div>
	)
})

interface TradeRowProps {
	trade: MarketTrade
	buckets: OutcomeBucket[]
}

const TradeRow = memo(function TradeRow({ trade, buckets }: TradeRowProps) {
	const bucket = buckets[trade.bucket]
	// Determine if "positive" outcome (e.g. Long)
	const isPositive = bucket?.direction === "up"
	const outcomeLabel = bucket?.label ?? BINARY_LABELS[trade.bucket] ?? `Bucket ${trade.bucket}`

	// avatarUrl is always provided by the backend (with DiceBear fallback applied server-side)
	const profileIdentifier = trade.username ?? trade.trader
	const explorerUrl = `${breakoutChain.blockExplorers?.default.url}/tx/${trade.txHash}`
	const sharesFormatted = formatShares(trade.shares)
	const timeAgo = formatTimeAgoSec(trade.blockTimestampSec)

	return (
		<div className="flex items-center gap-3 rounded-xl border border-transparent p-3 transition-all hover:border-border-subtle hover:bg-bg-secondary/50">
			<Link
				className="shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand focus-visible:ring-offset-2"
				params={{ identifier: profileIdentifier }}
				to="/u/$identifier"
			>
				<SimpleAvatar
					alt={`${trade.displayName}'s avatar`}
					border
					hoverEffect
					size="sm"
					src={trade.avatarUrl}
					variant="circle"
				/>
			</Link>

			<Link
				className="min-w-0 flex-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand focus-visible:ring-offset-2"
				params={{ identifier: profileIdentifier }}
				to="/u/$identifier"
			>
				<p className="text-sm text-text-secondary leading-relaxed">
					<span className="mr-1 font-semibold text-text-primary">@{trade.displayName}</span>
					<span
						className={cn("font-medium", trade.isBuy ? "text-text-positive" : "text-text-negative")}
					>
						{trade.isBuy ? "bought" : "sold"}
					</span>
					<span className="mx-1 font-bold text-text-primary">{sharesFormatted}</span>
					<span className="text-text-muted">shares of</span>
					<span
						className={cn(
							"ml-1 font-bold",
							isPositive ? "text-text-positive" : "text-text-negative",
						)}
					>
						{outcomeLabel}
					</span>
					<span className="ml-1 text-text-muted">at {trade.pricePerShare.toFixed(0)}¢</span>
				</p>
			</Link>

			<div className="flex shrink-0 items-center gap-1.5">
				<span className="text-text-muted text-xs tabular-nums">{timeAgo}</span>
				<a
					aria-label="View transaction on block explorer (opens in new tab)"
					className="rounded p-1 text-text-muted transition-colors hover:text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand focus-visible:ring-offset-2"
					href={explorerUrl}
					rel="noopener noreferrer"
					target="_blank"
				>
					<ExternalLink aria-hidden="true" className="size-4" />
				</a>
			</div>
		</div>
	)
})
